import { ChevronDownIcon, ChevronRightIcon, ChevronUpIcon } from '@schuettflix/icons-react';
import { Trans, useTranslation } from 'react-i18next';
import { useState } from 'react';
import { PRICING_STRATEGY } from '@schuettflix/interfaces';
import { cn } from '@/shared/utils/cn.ts';
import { productTypeToImageMap } from '../utils/productTypeToImageMap';
import { DISPOSAL_PURPOSE_LABEL, PRODUCT_CARD_NAME_LABEL } from '../utils/productCardLabelMaps';
import { ProductTemplateOption, ProductTemplateSelectedOption } from '@/shared/components/PresetSelection/types.ts';

const PRICING_STRATEGY_LABELS = {
    FRANCO: 'businessCases.franco.label',
    MERCHANT: 'businessCases.merchant.label',
    STANDARD: 'businessCases.standard.label',
} as const satisfies Record<PRICING_STRATEGY, string>;

const PRICING_STRATEGY_DESCRIPTIONS = {
    FRANCO: 'businessCases.franco.description',
    MERCHANT: 'businessCases.merchant.description',
    STANDARD: null,
} as const satisfies Record<PRICING_STRATEGY, string | null>;

type ProductWithTransportCardProps = {
    option: ProductTemplateOption;
    onSelect: (option: ProductTemplateSelectedOption) => void;
    isExpandable: boolean;
};

export const ProductWithTransportCard = ({ option, isExpandable, onSelect }: ProductWithTransportCardProps) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);

    const { productType, isStandalone, pricingStrategies, formPreset } = option;

    const imgSrc = productTypeToImageMap[productType];
    const title = `${t(PRODUCT_CARD_NAME_LABEL[productType])} ${t('productTemplate.includingTransport')}`;
    const description = formPreset?.disposalPurpose ? t(DISPOSAL_PURPOSE_LABEL[formPreset.disposalPurpose]) : undefined;

    const cardHeader = (
        <div className="flex flex-col">
            <span className="font-copy-md-strong text-interactive truncate" title={title}>
                {title}
            </span>
            {description && <span className="font-copy-md text-subdued">{description}</span>}
        </div>
    );

    return (
        <div
            className="bg-surface shadow-low grid cursor-pointer grid-cols-[80px_auto] gap-0 rounded"
            onClick={
                isExpandable
                    ? () => setExpanded(!expanded)
                    : () =>
                          onSelect({
                              productType,
                              isStandalone,
                              formPreset,
                          })
            }
            data-test={`productWithTransportCard-${productType}`}
        >
            <div
                className="bg-surface-subdued overflow-hidden rounded-l bg-repeat-y"
                style={{ backgroundImage: imgSrc ? `url(${imgSrc})` : undefined }}
            />
            {isExpandable ? (
                <div className="flex w-full flex-col">
                    <div className="flex items-center justify-between p-4">
                        {cardHeader}
                        {expanded ? <ChevronUpIcon size="xs" /> : <ChevronDownIcon size="xs" />}
                    </div>
                    <div
                        className={cn(
                            'grid transition-all duration-300 ease-in-out',
                            expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                        )}
                    >
                        <div className="overflow-hidden">
                            <PricingStrategySelection
                                pricingStrategies={pricingStrategies}
                                onSelect={pricingStrategy =>
                                    onSelect({
                                        productType,
                                        pricingStrategy,
                                        isStandalone,
                                        formPreset,
                                    })
                                }
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <div className="flex items-center justify-between p-4">{cardHeader}</div>
            )}
        </div>
    );
};

type PricingStrategySelectionProps = {
    pricingStrategies: PRICING_STRATEGY[];
    onSelect: (pricingStrategy: PRICING_STRATEGY) => void;
};

function PricingStrategySelection({ pricingStrategies, onSelect }: PricingStrategySelectionProps) {
    const { t } = useTranslation();
    return (
        <div>
            {pricingStrategies.map(strategy => (
                <div
                    key={strategy}
                    onClick={() => onSelect(strategy)}
                    className="hover:bg-surface-hovered px-4 pb-0 pt-4 last-of-type:rounded-br [&:last-of-type>*]:border-none [&:last-of-type>*]:pb-4"
                    data-test={`pricingStrategy-${strategy}`}
                >
                    <div className="flex items-center justify-between border-b pb-[15px]">
                        <div className="flex flex-col">
                            <p className="text-md">
                                <Trans t={t} i18nKey={PRICING_STRATEGY_LABELS[strategy]} components={{ type: <u /> }} />
                            </p>
                            {PRICING_STRATEGY_DESCRIPTIONS[strategy] && (
                                <span className="font-copy-sm text-subdued">
                                    {t(PRICING_STRATEGY_DESCRIPTIONS[strategy])}
                                </span>
                            )}
                        </div>
                        <ChevronRightIcon size="xs" />
                    </div>
                </div>
            ))}
        </div>
    );
}
